import React, { useMemo } from 'react'
import type { RundownCueOrderItem } from '@rundown-studio/types'
import { CueType } from '@rundown-studio/types'
import { useAtomValue } from 'jotai'
import { isGroupCollapsedAtomFamily } from '../../../store/rundown.store'
import { cn } from '@/utils'
import { draggingCueAtom } from '../../cues-list'

interface GroupTreeLinesProps {
  cueId: string
  cueType: CueType
  parentId?: string | null
  parentBackgroundColour: string | null
  lastSibling: boolean
  childrenCues?: RundownCueOrderItem[]
}

export const GroupTreeLines: React.FC<GroupTreeLinesProps> = ({
  cueId,
  cueType,
  parentId,
  lastSibling,
  parentBackgroundColour,
  childrenCues,
}) => {
  // Per-cue read so tree lines only re-render when *this* group toggles,
  // not when any group in the rundown does. See isGroupCollapsedAtomFamily.
  const isCollapsed = useAtomValue(isGroupCollapsedAtomFamily(cueId))
  const draggingCue = useAtomValue(draggingCueAtom)

  const groupLineColour = useMemo(() => {
    if (parentBackgroundColour === 'transparent') return

    return parentBackgroundColour ?? undefined
  }, [parentBackgroundColour])

  if (draggingCue?.id === cueId) return null

  // Early return if tree lines shouldn't be shown
  if (!parentId && cueType !== CueType.GROUP) {
    return null
  }

  const isVisibleCueType = [CueType.CUE, CueType.HEADING].includes(cueType)
  const isGroupWithoutChildren =
    cueType === CueType.GROUP && !childrenCues?.length

  const lineColorClass = groupLineColour ? 'brightness-150' : 'bg-gray-700'
  const textColorClass = groupLineColour ? 'brightness-150' : 'text-gray-700'
  const lineOpacity = isVisibleCueType ? 'opacity-100' : 'opacity-0'

  const topLineHeight = lastSibling ? 'h-[52px] -top-1' : 'h-[80%] -top-6'
  const shouldShowBottomLine =
    !lastSibling && !isCollapsed && !isGroupWithoutChildren

  const bottomLinePosition = isVisibleCueType ? 'bottom-0' : '-bottom-10'
  const bottomLineOpacity = shouldShowBottomLine ? 'opacity-100' : 'opacity-0'

  return (
    <>
      {/* Top vertical line */}
      <div
        style={{ backgroundColor: groupLineColour }}
        className={cn(
          'absolute left-[20px] w-[4px] -top-[2px] -z-20 pointer-events-none',
          lineColorClass,
          lineOpacity,
          topLineHeight,
        )}
      />

      {/* Curved connector */}
      <CurvedConnector
        groupLineColour={groupLineColour}
        textColorClass={textColorClass}
        opacity={lineOpacity}
      />

      {/* Bottom vertical line */}
      {!lastSibling && (
        <div
          style={{ backgroundColor: groupLineColour }}
          className={cn(
            'absolute left-[20px] w-[4px] h-1/2 -z-20 pointer-events-none',
            lineColorClass,
            bottomLinePosition,
            bottomLineOpacity,
          )}
        />
      )}
    </>
  )
}

interface CurvedConnectorProps {
  groupLineColour?: string
  textColorClass: string
  opacity: string
}

const CurvedConnector: React.FC<CurvedConnectorProps> = ({
  groupLineColour,
  textColorClass,
  opacity,
}) => (
  <svg
    width="15"
    height="12"
    viewBox="0 0 15 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: groupLineColour }}
    className={cn(
      'absolute left-[20px] top-13 -translate-y-1/2 pointer-events-none',
      textColorClass,
      opacity,
    )}
  >
    <path d="M2 0C2 10 15 10 15 10" stroke="currentColor" strokeWidth={4} />
  </svg>
)

export default GroupTreeLines
